// Command registry backbone: ONE table describing the editor's tools
// (id, icon, label, key, usage hint) plus the shortcut-matching rules,
// consumed by the toolbar, the global keyboard handler and — later —
// the context menu / command palette. Adding a tool here wires up its
// button, its shortcut and its tooltip in one place.
import type { ShapeKind } from "./protocol.ts";
import type { Tool } from "./ui-types.ts";

/** A keyboard binding. `key` compares against KeyboardEvent.key —
 *  case-sensitively without `mod` (so plain "v" ignores Shift+V, which
 *  types "V"), case-insensitively with it (Ctrl+Z and Ctrl+Shift+Z both
 *  report "z"/"Z"). `shift` is only enforced when specified. */
export interface Shortcut {
  key: string | string[];
  /** Requires Ctrl (or Cmd on macOS). */
  mod?: boolean;
  shift?: boolean;
}

export function shortcutMatches(e: KeyboardEvent, s: Shortcut | Shortcut[]): boolean {
  for (const one of Array.isArray(s) ? s : [s]) {
    const mod = e.metaKey || e.ctrlKey;
    if ((one.mod ?? false) !== mod) continue;
    if (one.shift !== undefined && one.shift !== e.shiftKey) continue;
    const keys = Array.isArray(one.key) ? one.key : [one.key];
    const pressed = one.mod ? e.key.toLowerCase() : e.key;
    if (keys.some((k) => (one.mod ? k.toLowerCase() : k) === pressed)) return true;
  }
  return false;
}

/** True when the event target is a place the user types. */
export function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return Boolean(
    el &&
      (el.isContentEditable ||
        el.tagName === "INPUT" ||
        el.tagName === "TEXTAREA" ||
        el.tagName === "SELECT")
  );
}

/** Which cluster a command belongs to. The toolbar, the command palette
 *  and (on a phone) the hamburger drawer are three views of ONE table,
 *  so a command added here appears in all of them rather than being
 *  hand-written into each surface. */
export type CommandGroup = "file" | "edit" | "view" | "panel" | "document" | "help";

/** One editor command: keyboard binding + metadata for menus. */
export interface EditorCommand {
  /** Stable dotted id, e.g. "tool.select", "edit.undo". */
  id: string;
  label: string;
  icon?: string;
  /** Cluster for the toolbar / drawer. Absent = palette and keyboard
   *  only, which is right for internals like the arrow-key nudges. */
  group?: CommandGroup;
  /** Higher sorts first within the group, and is what survives when a
   *  narrow toolbar can only show some of it. Default 0. */
  priority?: number;
  /** Whether the command is currently "on" — drives aria-pressed on the
   *  rendered button (an open panel, an active mode). */
  active?: boolean;
  /** Greys the button out; the command still runs from the palette if
   *  its own `when` allows. */
  disabled?: boolean;
  shortcut?: Shortcut | Shortcut[];
  /** Fire even while focus is in an input (default: inputs swallow it). */
  global?: boolean;
  /** Call preventDefault before running (default false — matches the
   *  legacy handler, which only suppressed browser defaults for a few
   *  combos). */
  prevent?: boolean;
  /** Extra availability check. Receives the keyboard event when
   *  dispatched from a shortcut, or null when invoked from the command
   *  palette / a menu. */
  when?: (e: KeyboardEvent | null) => boolean;
  /** false hides the command from the palette (nudges, internals). */
  inPalette?: boolean;
  run: () => void;
}

/** Human-readable label for a command's (first) shortcut. */
export function shortcutLabel(s: Shortcut | Shortcut[] | undefined): string {
  if (!s) return "";
  const one = Array.isArray(s) ? s[0] : s;
  if (!one) return "";
  const key = Array.isArray(one.key) ? one.key[0]! : one.key;
  const nice =
    key === "Escape" ? "Esc" : key === "Delete" ? "Del" : key.length === 1 ? key.toUpperCase() : key;
  return `${one.mod ? "Ctrl+" : ""}${one.shift ? "Shift+" : ""}${nice}`;
}

/** A group's commands, most important first. This is what a toolbar
 *  cluster or a drawer section renders from. */
export function commandGroup(
  commands: readonly EditorCommand[],
  group: CommandGroup
): EditorCommand[] {
  return commands
    .filter((c) => c.group === group)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
}

/** Run the first matching command. Returns true when one ran. */
export function dispatchShortcut(e: KeyboardEvent, commands: readonly EditorCommand[]): boolean {
  const inInput = isEditableTarget(e.target);
  for (const c of commands) {
    if (!c.shortcut || !shortcutMatches(e, c.shortcut)) continue;
    if (inInput && !c.global) continue;
    if (c.when && !c.when(e)) continue;
    if (c.prevent) e.preventDefault();
    c.run();
    return true;
  }
  return false;
}

// ─────────────────────────── tool table ───────────────────────────

export interface ToolDef {
  tool: Tool;
  icon: string;
  label: string;
  /** Plain-letter shortcut (lowercase). Absent = button only. */
  key?: string;
  /** Usage hint appended to the tooltip after the key. */
  hint?: string;
  /** Stay armed after making something, instead of springing back to
   *  the pointer. True for the tools people genuinely repeat — you draw
   *  several strokes and mark several phrases, but you rarely place two
   *  crop boxes. Holding a tool button locks any tool for repeats. */
  sticky?: boolean;
}

/** A cluster of tools that answer the same question, behind one button
 *  that remembers your last pick. Twenty-one flat buttons is a list to
 *  hunt through; nobody looks for "squiggly", they look for a way to
 *  mark up text. The button shows the last member you used, so the
 *  common case is still one click. */
export interface ToolGroup {
  id: string;
  label: string;
  icon: string;
  members: readonly ToolDef[];
}

/** Toolbar order, left of the shape menu. */
export const PRIMARY_TOOLS: readonly ToolDef[] = [
  {
    tool: "select",
    icon: "ri-cursor-line",
    label: "Select",
    key: "v",
    hint: "click text to edit it in place; click any other object to select it. Drag from empty space to marquee; drag from a word to select text. Ctrl/Cmd+click adds to the selection",
  },
  {
    tool: "hand",
    icon: "ri-hand",
    label: "Hand",
    key: "p",
    hint: "drag to pan the page; hold Space or middle-drag to pan from any tool",
  },
  { tool: "text", icon: "ri-text", label: "Text", key: "t" },
  { tool: "highlight", icon: "ri-mark-pen-line", label: "Highlight", key: "h", sticky: true },
  { tool: "underline", icon: "ri-underline", label: "Underline", key: "u", sticky: true },
  { tool: "strikeout", icon: "ri-strikethrough", label: "Strikeout", key: "s", sticky: true },
  { tool: "squiggly", icon: "ri-pulse-line", label: "Squiggly", key: "q", sticky: true },
  { tool: "ink", icon: "ri-pencil-line", label: "Draw", key: "d", sticky: true },
];

/** Toolbar order, right of the shape menu. */
export const SECONDARY_TOOLS: readonly ToolDef[] = [
  { tool: "note", icon: "ri-sticky-note-line", label: "Note", key: "n" },
  {
    tool: "measure",
    icon: "ri-ruler-line",
    label: "Measure",
    key: "m",
    hint: "drag between two points; pick units or calibrate against a known length",
    sticky: true,
  },
  { tool: "whiteout", icon: "ri-eraser-line", label: "Whiteout", key: "w" },
  {
    tool: "bgblock",
    icon: "ri-stack-line",
    label: "Block",
    hint: "drag a rectangle — it is drawn BEHIND the page content (press an existing block with Select to recolour it)",
  },
  {
    tool: "link",
    icon: "ri-link",
    label: "Link",
    key: "k",
    hint: "drag a clickable area, then choose a URL or a page to jump to",
  },
  {
    tool: "crop",
    icon: "ri-crop-line",
    label: "Crop",
    key: "c",
    hint: "drag the area to keep; nothing is deleted, the page box is trimmed",
  },
  {
    tool: "field",
    icon: "ri-input-field",
    label: "Field",
    key: "f",
    hint: "drag a box to add a fillable text field, checkbox or dropdown",
  },
  { tool: "redact", icon: "ri-checkbox-blank-fill", label: "Redact", key: "x", sticky: true },
];

/** The ToolButton tooltip's parenthetical: "V", "E — click a line…",
 *  or just the hint for key-less tools. */
export function toolShortcutText(d: ToolDef): string {
  if (!d.key) return d.hint ?? "";
  return d.hint ? `${d.key.toUpperCase()} — ${d.hint}` : d.key.toUpperCase();
}

export const SHAPE_META: Record<ShapeKind, { icon: string; label: string; shortcut: string }> = {
  rect: { icon: "ri-rectangle-line", label: "Rectangle", shortcut: "R" },
  ellipse: { icon: "ri-circle-line", label: "Ellipse", shortcut: "O" },
  line: { icon: "ri-subtract-line", label: "Line", shortcut: "L" },
  arrow: { icon: "ri-arrow-right-up-line", label: "Arrow", shortcut: "A" },
};

/** The tool palette as five verbs plus the two persistent modes. Every
 *  tool in PRIMARY_TOOLS / SECONDARY_TOOLS appears exactly once here —
 *  this regroups them, it doesn't drop any. Shapes are members of "Add"
 *  rather than a menu of their own, since placing a rectangle and
 *  placing a text box are the same intent. */
export const TOOL_GROUPS: readonly ToolGroup[] = [
  {
    id: "markup",
    label: "Mark up",
    icon: "ri-mark-pen-line",
    members: [
      toolDef("highlight"),
      toolDef("underline"),
      toolDef("strikeout"),
      toolDef("squiggly"),
      toolDef("ink"),
    ],
  },
  {
    id: "add",
    label: "Add",
    icon: "ri-add-box-line",
    members: [
      toolDef("text"),
      toolDef("note"),
      ...(Object.keys(SHAPE_META) as ShapeKind[]).map((k) => ({
        tool: k,
        icon: SHAPE_META[k].icon,
        label: SHAPE_META[k].label,
        key: SHAPE_META[k].shortcut.toLowerCase(),
      })),
      toolDef("link"),
      toolDef("field"),
    ],
  },
  {
    id: "hide",
    label: "Hide",
    icon: "ri-eraser-line",
    members: [
      toolDef("whiteout"),
      toolDef("bgblock"),
      toolDef("redact"),
      toolDef("crop"),
    ],
  },
  {
    id: "measure",
    label: "Measure",
    icon: "ri-ruler-line",
    members: [toolDef("measure")],
  },
];

/** Look a tool up in the flat tables — the groups above are a VIEW of
 *  those tables, so a tool's shortcut, hint and stickiness stay defined
 *  in exactly one place. */
export function toolDef(tool: Tool): ToolDef {
  const d = [...PRIMARY_TOOLS, ...SECONDARY_TOOLS].find((x) => x.tool === tool);
  if (!d) throw new Error(`no ToolDef for ${tool}`);
  return d;
}

/** Which group holds a tool (null for the pointer and the hand, which
 *  are modes rather than verbs). */
export function groupOfTool(tool: Tool): ToolGroup | null {
  return TOOL_GROUPS.find((g) => g.members.some((m) => m.tool === tool)) ?? null;
}

/** How to NAME a tool in the UI. Unlike `toolDef` this never throws:
 *  shapes live in SHAPE_META rather than the flat tables, so anything
 *  that labels "whatever is armed" — the options pane header, the
 *  phone's armed-tool chip — has to look through the groups too. */
export function toolMeta(tool: Tool): { icon: string; label: string } {
  const flat = [...PRIMARY_TOOLS, ...SECONDARY_TOOLS].find((x) => x.tool === tool);
  if (flat) return { icon: flat.icon, label: flat.label };
  const member = groupOfTool(tool)?.members.find((m) => m.tool === tool);
  return member
    ? { icon: member.icon, label: member.label }
    : { icon: "ri-cursor-line", label: tool };
}

/** Does this tool stay armed after it makes something? Shapes aren't in
 *  the flat tables (they come from SHAPE_META), so look them up through
 *  the groups and default to one-shot. */
export function toolIsSticky(tool: Tool): boolean {
  const g = groupOfTool(tool);
  return g?.members.find((m) => m.tool === tool)?.sticky ?? false;
}
